const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtCompact = (n) => {
  const v = Number(n ?? 0);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};
const fmtPct = (n, digits = 1) => `${Number(n ?? 0).toFixed(digits)}%`;

const BAR_COLORS = ["", "blue", "green", "orange"];
const PROGRAM_META = {
  "CTET Qualification": {
    iconClass: "red",
    tag: "Highest",
    tagClass: "red",
    barColor: "var(--ctex-danger)",
    displayAsPct: true,
    description: "Teachers not yet qualified need targeted certification support.",
    owner: "Block Education Officer",
    priority: "Certification support",
    status: "Critical",
    statusClass: "red",
  },
  "CWSN Training": {
    iconClass: "",
    tag: "Large",
    tagClass: "blue",
    barColor: "var(--ctex-primary)",
    displayAsPct: false,
    description: "Inclusive education capacity building is the second-largest demand pool.",
    owner: "Training Coordinator",
    priority: "Inclusive classroom readiness",
    status: "High",
    statusClass: "",
  },
  "NISHTHA Training": {
    iconClass: "green",
    tag: "Backlog",
    tagClass: "",
    barColor: "var(--ctex-accent)",
    displayAsPct: false,
    description: "Pending teachers can be organized into block-wise completion batches.",
    owner: "Cluster Resource Centre",
    priority: "Batch completion",
    status: "High",
    statusClass: "",
  },
  "Computer Training": {
    iconClass: "orange",
    tag: "Focused",
    tagClass: "green",
    barColor: "var(--ctex-secondary)",
    displayAsPct: false,
    description: "Digital enablement demand is smaller, suitable for focused school clusters.",
    owner: "ICT Nodal Officer",
    priority: "Digital skills",
    status: "Focused",
    statusClass: "green",
  },
};

export const buildTrainingReadinessPct = (certification = null) =>
  certification?.nishtha?.completed_pct ?? 0;

export const buildTrainingGaugeStyle = (readinessPct = 0) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--ctex-danger) 0 ${readinessPct}%, rgba(239,68,68,.12) ${readinessPct}% 100%)`,
});

export const buildTrainingHeroCopy = (certification = null, trainingData = []) => {
  const nishthaPct = certification?.nishtha?.completed_pct ?? 0;
  const nishthaDone = certification?.nishtha?.completed ?? 0;
  const unknown = certification?.ctet?.unknown ?? 0;
  const ctetPct = certification?.ctet?.qualified_pct ?? 0;
  const demandTypes = (trainingData || []).length;
  return {
    headline: "Training Readiness Needs Acceleration",
    narrative:
      "NISHTHA completion is progressing, but CTET qualification is still a critical certification gap. The dashboard now separates completion, unknowns, and demand into action-ready views.",
    chips: [
      { label: "High Certification Gap", className: "" },
      { label: `${demandTypes} Training Demand Types`, className: "blue" },
      { label: `${fmt(nishthaDone)} NISHTHA Completed`, className: "green" },
    ],
    primaryAction: `Launch a CTET qualification support sprint and close the ${fmt(unknown)} unknown CTET records before the next monthly review.`,
    readinessPct: nishthaPct,
    ctetGap: ctetPct < 15,
  };
};

export const buildTrainingHeroKpis = (certification = null, trainingData = []) => [
  {
    label: "CTET Qualified",
    value: fmt(certification?.ctet?.qualified),
    sub: `${fmtPct(certification?.ctet?.qualified_pct)} of teacher records`,
    iconClass: "green",
  },
  {
    label: "CTET Unknown",
    value: fmt(certification?.ctet?.unknown),
    sub: "Records requiring verification",
    iconClass: "orange",
  },
  {
    label: "NISHTHA Completed",
    value: fmt(certification?.nishtha?.completed),
    sub: `${fmtPct(certification?.nishtha?.completed_pct)} completion coverage`,
    iconClass: "",
  },
  {
    label: "Training Demand",
    value: String((trainingData || []).length),
    sub: "Priority training categories",
    iconClass: "purple",
  },
];

export const buildCtetDonutView = (certification = null) => {
  const ctet = certification?.ctet ?? {};
  const qualified = ctet.qualified ?? 0;
  const unknown = ctet.unknown ?? 0;
  const notQualified = ctet.not_qualified ?? 0;
  const total = qualified + unknown + notQualified || 1;
  const qualifiedPct = (qualified / total) * 100;
  const unknownPct = (unknown / total) * 100;
  const notQualifiedPct = (notQualified / total) * 100;
  const qEnd = qualifiedPct;
  const uEnd = qEnd + unknownPct;
  return {
    centerPct: fmtPct(ctet.qualified_pct ?? qualifiedPct),
    style: {
      background: `conic-gradient(var(--ctex-success) 0 ${qEnd}%, var(--ctex-warning) ${qEnd}% ${uEnd}%, var(--ctex-danger) ${uEnd}% 100%)`,
    },
    legend: [
      {
        label: "Qualified",
        dotClass: "green",
        value: `${fmt(qualified)} · ${fmtPct(qualifiedPct)}`,
      },
      {
        label: "Unknown",
        dotClass: "orange",
        value: `${fmt(unknown)} · ${fmtPct(unknownPct)}`,
      },
      {
        label: "Not Qualified",
        dotClass: "red",
        value: `${fmt(notQualified)} · ${fmtPct(notQualifiedPct)}`,
      },
    ],
  };
};

export const buildNishthaDonutView = (certification = null) => {
  const nishtha = certification?.nishtha ?? {};
  const completed = nishtha.completed ?? 0;
  const pending = nishtha.not_completed ?? 0;
  const total = completed + pending || 1;
  const completedPct = nishtha.completed_pct ?? (completed / total) * 100;
  const gapPct = Math.round((100 - completedPct) * 10) / 10;
  return {
    centerPct: fmtPct(completedPct),
    style: {
      background: `conic-gradient(var(--ctex-primary) 0 ${completedPct}%, #94a3b8 ${completedPct}% 100%)`,
    },
    legend: [
      { label: "Completed", dotClass: "", value: fmt(completed) },
      { label: "Pending / Not Completed", dotClass: "gray", value: fmt(pending) },
      { label: "Completion Gap", dotClass: "orange", value: fmtPct(gapPct) },
    ],
  };
};

export const buildTrainingDemandBars = (trainingData = [], certification = null) => {
  const sorted = [...(trainingData || [])].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  const max = Math.max(...sorted.map((r) => r.count ?? 0), 1);
  return sorted.map((row, idx) => ({
    label: row.training_type?.replace(" ", "\n") ?? row.training_type,
    displayLabel: row.training_type,
    count: row.count ?? 0,
    display: fmtCompact(row.count),
    width: Math.max(12, Math.round(((row.count ?? 0) / max) * 90)),
    barClass: BAR_COLORS[idx] ?? "",
  }));
};

export const buildProgramCards = (trainingData = [], certification = null) => {
  const sorted = [...(trainingData || [])].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  const max = Math.max(...sorted.map((r) => r.count ?? 0), 1);
  const ctet = certification?.ctet ?? {};
  const total = (ctet.qualified ?? 0) + (ctet.unknown ?? 0) + (ctet.not_qualified ?? 0) || 1;
  const notQualifiedPct = Math.round(((ctet.not_qualified ?? 0) / total) * 1000) / 10;

  return sorted.slice(0, 4).map((row) => {
    const meta = PROGRAM_META[row.training_type] ?? {
      iconClass: "",
      tag: "Program",
      tagClass: "",
      barColor: "var(--ctex-primary)",
      displayAsPct: false,
      description: "Training demand category for district capacity planning.",
      owner: "Training Team",
      priority: "Capacity building",
      status: "Monitor",
      statusClass: "",
    };
    const width = meta.displayAsPct
      ? notQualifiedPct
      : Math.max(8, Math.round(((row.count ?? 0) / max) * 100));
    const display = meta.displayAsPct ? fmtPct(notQualifiedPct) : fmtCompact(row.count);
    return {
      title: row.training_type,
      display,
      description: meta.description,
      iconClass: meta.iconClass,
      tag: meta.tag,
      tagClass: meta.tagClass,
      barWidth: width,
      barColor: meta.barColor,
    };
  });
};

export const buildProgramWatchlist = (trainingData = [], certification = null) => {
  const sorted = [...(trainingData || [])].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  const ctet = certification?.ctet ?? {};
  return sorted.slice(0, 4).map((row, idx) => {
    const meta = PROGRAM_META[row.training_type] ?? {
      owner: "Training Team",
      priority: "Capacity building",
      status: "Monitor",
      statusClass: "",
    };
    const gap =
      row.training_type === "CTET Qualification"
        ? ctet.not_qualified ?? row.count
        : row.count;
    return {
      rank: idx + 1,
      program: row.training_type,
      gap: fmt(gap),
      priority: meta.priority,
      owner: meta.owner,
      status: meta.status,
      statusClass: meta.statusClass,
    };
  });
};

export const buildTrainingActions = (certification = null) => {
  const unknown = certification?.ctet?.unknown ?? 0;
  const pending = certification?.nishtha?.not_completed ?? 0;
  return [
    {
      title: "Close CTET Unknown Records",
      body: `Verify ${fmt(unknown)} records first so the district can separate data quality gaps from true qualification gaps.`,
      iconClass: "red",
    },
    {
      title: "Run CTET Support Sprint",
      body: "Prioritize the largest non-qualified blocks for focused coaching, study material, and certification tracking.",
      iconClass: "purple",
    },
    {
      title: "Batch-Plan NISHTHA Completion",
      body: `Convert the ${fmt(pending)} pending teachers into weekly block-wise batches with escalation for low completion schools.`,
      iconClass: "green",
    },
  ];
};
